import { styled } from '@grupoboticario/flora-react-system'

export const StyledCheckboxWrapper = styled('div', {
  position: 'relative',
  display: 'flex',
})

export const StyledCheckboxInput = styled('input', {
  opacity: 0,
  appearance: 'none',
  position: 'absolute',

  '&:checked + label span:after': {
    top: '45%',
    transform: 'translate(-50%, -50%) rotate(-45deg)',
    width: '12px',
    height: '6px',
    border: '2px solid',
    borderTop: 'none',
    borderRight: 'none',
  },

  '&:indeterminate + label span:after': {
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: '12px',
    height: '2px',
    backgroundColor: 'currentColor',
  },

  '&:focus-visible + label span': {
    outlineStyle: 'solid',
    outlineWidth: '2px',
    outlineOffset: '2px',
  },

  '&:disabled, &[aria-disabled="true"]': {
    pointerEvents: 'none',
    cursor: 'not-allowed',
    '&, &:checked, &:indeterminate, &:hover, &:focus-visible, &:hover:not(:focus-visible)': {
      '& + label span': {
        pointerEvents: 'none',
        cursor: 'not-allowed',
        boxShadow: 'none',
      },
    },
  },

  variants: {
    /**
     * Opcional. Especifica o estilo visual do checkbox de acordo com o status
     */
    status: {
      neutral: {},
      error: {},
    },

    /**
     * Opcional, especifica o tamanho da label do checkbox
     */
    textSize: {
      small: {
        '& + label': {
          fontSize: '$auxiliar',
        },
      },
      medium: {
        '& + label': {
          fontSize: '$bodySmall',
        },
      },
      large: {
        '& + label': {
          fontSize: '$bodyLarge',
        },
      },
    },
    /**
     * Opcional, especifica um espaçamento de identação, a esquerda do Checkbox
     */
    hasIdentation: {
      true: {
        '& + label': {
          marginLeft: '$7',
        },
      },
    },

    /**
     * Opcional, adapta cores do checkbox para aplicações em superfícies light ou dark.
     */
    surfaceColor: {
      light: {
        '& + label': {
          color: '$nonInteractivePredominant',
        },

        '&:focus-visible + label span': {
          outlineColor: '$keyboardFocusDefault',
        },

        '&:disabled, &[aria-disabled="true"]': {
          '&:checked': {
            '& + label span': {
              color: '$disabledContent',
              backgroundColor: '$disabledBackground',
              borderColor: 'transparent',
            },
          },
          '&, &:indeterminate, &:hover, &:focus-visible, &:hover:not(:focus-visible)': {
            '& + label span': {
              color: '$disabledContent',
              backgroundColor: '$disabledBackground',
              borderColor: '$disabledContent',
            },

            '& + label ': {
              color: '$disabledContent',
            },
          },
        },
      },
      dark: {
        '& + label': {
          color: '$nonInteractiveAltPredominant',
        },

        '&:focus-visible + label span': {
          outlineColor: '$keyboardFocusAlt',
        },

        '&:disabled, &[aria-disabled="true"]': {
          '&:checked': {
            '& + label span': {
              color: '$disabledAltContent',
              backgroundColor: '$disabledAltBackground',
              borderColor: 'transparent',
            },
          },
          '&, &:indeterminate, &:hover, &:focus-visible, &:hover:not(:focus-visible)': {
            '& + label span': {
              color: '$disabledAltContent',
              backgroundColor: '$disabledAltBackground',
              borderColor: '$disabledAltContent',
            },

            '& + label ': {
              color: '$disabledAltContent',
            },
          },
        },
      },
    },
  },

  compoundVariants: [
    {
      status: 'neutral',
      surfaceColor: 'light',
      css: {
        '& + label': {
          span: {
            borderColor: '$actionableDefault',
          },
        },

        '&:checked + label span': {
          backgroundColor: '$actionableDefault',
          borderColor: '$actionableDefault',
          color: '$actionableContent',
        },

        '&:indeterminate + label span': {
          borderColor: '$actionableDefault',
          color: '$actionableDefault',
        },

        '&:hover + label span': {
          boxShadow: ' 0 0 0 4px $colors$nonPrimaryButtonHover',
          backgroundColor: '$actionableHover',
          borderColor: '$actionableHover',
        },

        '&:not(:checked):hover + label span': {
          backgroundColor: '$nonPrimaryButtonHover',
          borderColor: '$actionableHover',
        },
      },
    },
    {
      status: 'neutral',
      surfaceColor: 'dark',
      css: {
        '& + label': {
          span: {
            borderColor: '$nonInteractiveAltAuxiliar',
          },
        },

        '&:checked + label span': {
          backgroundColor: '$actionableAlt',
          borderColor: '$actionableAlt',
          color: '$actionableAltContent',
        },

        '&:indeterminate + label span': {
          borderColor: '$actionableAlt',
          color: '$actionableAlt',
        },

        '&:hover + label span': {
          boxShadow: ' 0 0 0 4px $colors$nonPrimaryButtonAltHover',
        },

        '&:not(:checked):hover + label span': {
          backgroundColor: '$nonPrimaryButtonAltHover',
          borderColor: '$actionableAlt',
        },
      },
    },
    {
      status: 'error',
      surfaceColor: 'light',
      css: {
        '& + label': {
          span: {
            borderColor: '$statusErrorActionableDefault',
          },
        },

        '&:checked + label span': {
          backgroundColor: '$statusErrorActionableDefault',
          borderColor: '$statusErrorActionableDefault',
          color: '$statusErrorActionableContent',
        },

        '&:indeterminate + label span': {
          borderColor: '$statusErrorActionableDefault',
          color: '$statusErrorActionableDefault',
        },

        '&:hover + label span': {
          boxShadow: ' 0 0 0 4px $colors$statusErrorNonPrimaryButtonHover',
          backgroundColor: '$statusErrorActionableHover',
          borderColor: '$statusErrorActionableHover',
        },

        '&:not(:checked):hover + label span': {
          backgroundColor: '$statusErrorNonPrimaryButtonHover',
          borderColor: '$statusErrorActionableHover',
        },
      },
    },
    {
      status: 'error',
      surfaceColor: 'dark',
      css: {
        '& + label': {
          span: {
            borderColor: '$statusErrorActionableAlt',
          },
        },

        '&:checked + label span': {
          backgroundColor: '$statusErrorActionableAlt',
          borderColor: '$statusErrorActionableAlt',
          color: '$statusErrorActionableAltContent',
        },

        '&:indeterminate + label span': {
          borderColor: '$statusErrorActionableAlt',
          color: '$statusErrorActionableAlt',
        },

        '&:hover + label span': {
          boxShadow: ' 0 0 0 4px $colors$statusErrorNonPrimaryButtonAltHover',
        },

        '&:not(:checked):hover + label span': {
          backgroundColor: '$statusErrorNonPrimaryButtonAltHover',
          borderColor: '$statusErrorActionableAltHover',
        },
      },
    },
  ],
})

export const StyledCheckboxLabel = styled('label', {
  position: 'relative',
  cursor: 'pointer',
  gap: '$2',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',

  variants: {
    disabled: {
      true: {
        cursor: 'not-allowed',
        pointerEvents: 'none',
      },
    },
  },
})

export const StyledCheckboxSpan = styled('span', {
  position: 'relative',
  display: 'block',
  width: '1.5rem',
  height: '1.5rem',
  flexShrink: 0,
  border: '2px solid',
  borderRadius: '$small',
  color: 'transparent',
  boxShadow: '0 0 0 4px transparent',
  transitionProperty: 'background-color, border-color, color, box-shadow',
  transitionDuration: '200ms',
  transitionTimingFunction: 'ease-out',

  '&:after': {
    content: '',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
  variants: {
    hasIdentation: {
      true: {
        marginLeft: '$7',
      },
    },
  },
})
